// Wording shared by the Studio pages. The Studio service answers in English;
// these turn its codes into the page's language (Spanish by default), keeping
// the service's own message as the fallback for a code this page predates.

// The version of the Studio plan terms the checkout and the plan change show
// (the studio.plans.terms.* strings in both languages). Bump it whenever that
// wording or the terms change, IN STEP with PLAN_TERMS_VERSION in
// backend/src/services/studioPlanService.js: the backend refuses a checkout or
// a change from a page showing other wording (409 terms_changed).
export const PLAN_TERMS_VERSION = "studio-plans-2026-10-29";

/**
 * The Intl locale for prices and dates. English is en-US. Spanish follows the
 * country: es-MX, es-CR, and es-US for the US, its territories or no country.
 */
export const localeFor = (lang, country) => {
  if (!String(lang || "").toLowerCase().startsWith("es")) return "en-US";
  const c = String(country || "").toUpperCase();
  if (c === "MX") return "es-MX";
  if (c === "CR") return "es-CR";
  return "es-US";
};

export const errorText = (t, err) =>
  t(`studio.errors.${err?.code || "unknown"}`, { defaultValue: err?.message || t("studio.errors.unknown") });

/** The site language as "es" or "en". */
export const siteLang = (i18n) => (String(i18n?.language || "en").toLowerCase().startsWith("es") ? "es" : "en");

/**
 * The language of a plan contract: Spanish for Mexico and Costa Rica whatever
 * the site language (LFPC arts. 34 and 85; Costa Rica wants Spanish terms),
 * otherwise the site language.
 */
export const contractLang = (lang, country) => (country === "MX" || country === "CR" ? "es" : lang === "es" ? "es" : "en");

/** A t() for one language. Falls back to the page's t where there is no i18next instance (tests). */
export const fixedT = (i18n, t, lang) => (typeof i18n?.getFixedT === "function" ? i18n.getFixedT(lang) : t);

// After a lapse: 7 days of grace, then the sites go offline; apps are kept 90
// days after that (LAPSE in studio/src/plans.js).
export const KEEP_DAYS = 90;

/** An ISO date `days` later, or null. */
export const addDays = (iso, days) => {
  const ms = Date.parse(iso || "");
  return Number.isFinite(ms) ? new Date(ms + days * 86_400_000).toISOString() : null;
};

/** "Free trial", "Starter" or "Business" ("Prueba gratis" in Spanish). */
export const tierLabel = (t, tier) => (tier ? t(`studio.plans.tiers.${tier}`, { defaultValue: tier }) : "");

/**
 * A plan's monthly price as one sentence, by how tax works for it: plus sales
 * tax (US), VAT included (MX) or the total price (CR). Always with the
 * currency code.
 */
export const priceLine = (t, price, locale) => {
  if (!price) return "";
  const amount = formatPlanPrice(price, locale);
  if (price.tax === "inclusive") return t("studio.plans.terms.priceInclusive", { price: amount });
  if (price.tax === "none") return t("studio.plans.terms.priceNone", { price: amount });
  return t("studio.plans.terms.priceExclusive", { price: amount });
};

/**
 * The same, as a phrase inside a sentence (no final period): "USD 59.00 per
 * month, plus sales tax where it applies", "MXN 999.00 al mes, IVA incluido",
 * "USD 59.00 al mes (precio total)". The plan change texts use it, so they
 * state the tax the way the checkout and the backend's consent text do.
 */
export const pricePhrase = (t, price, locale) => {
  if (!price) return "";
  const amount = formatPlanPrice(price, locale);
  if (price.tax === "inclusive") return t("studio.plan.change.pricePhraseInclusive", { price: amount });
  if (price.tax === "none") return t("studio.plan.change.pricePhraseNone", { price: amount });
  return t("studio.plan.change.pricePhraseExclusive", { price: amount });
};

/**
 * What a plan from GET /studio/plans includes, as short lines for a card and
 * for the plan line of the terms. No dollar amounts.
 */
export function planFeatures(t, plan) {
  if (!plan) return [];
  if (plan.tier === "free") {
    return [
      t("studio.plans.features.messagesTrial", { count: plan.messages, perDay: plan.perDay, days: plan.days }),
      t("studio.plans.features.standardOnly"),
      t("studio.plans.features.previewOnly"),
    ];
  }
  const lines = [
    t("studio.plans.features.messagesMonth", { count: plan.messages }),
    t("studio.plans.features.perHour", { count: plan.perHour }),
    (plan.models || []).includes("advanced")
      ? t("studio.plans.features.standardAdvanced", { uses: plan.advancedCost ?? 2 })
      : t("studio.plans.features.standardOnly"),
    t("studio.plans.features.liveApps", { count: plan.liveApps }),
    t("studio.plans.features.hosting"),
    t("studio.plans.features.fixes", { count: plan.fixesPerMonth }),
  ];
  if (plan.customDomains === "one") lines.push(t("studio.plans.features.domainOne"));
  if (plan.customDomains === "per_app") lines.push(t("studio.plans.features.domainPerApp"));
  if (plan.supportBusinessDays) lines.push(t("studio.plans.features.support", { count: plan.supportBusinessDays }));
  return lines;
}

/** One short label and a chip style for a project's state. */
export function projectStatus(t, project) {
  if (project.liveUrl && !project.liveDisabled) return { label: t("studio.status.live"), chip: "studio-chip-live" };
  if (project.status === "ready") return { label: t("studio.status.ready"), chip: "studio-chip-done" };
  if (project.status === "provisioning") return { label: t("studio.status.settingUp"), chip: "" };
  if (project.status === "error") return { label: t("studio.status.needsAttention"), chip: "" };
  return { label: t("studio.status.draft"), chip: "" };
}

/**
 * Whether a PlanView is the free trial. Its resetsAt is the day the trial
 * ENDS: nothing resets then, so the wording below never says "reset" for it.
 */
export const isTrialPlan = (plan) => plan?.source === "trial" || Boolean(plan?.trial);

/** "AI chat is paused until {date}..." or, on the trial, "...for the rest of your free trial". */
export function capacityPausedText(t, plan, resetsAt, locale) {
  const date = resetsAt ? formatDay(resetsAt, locale) : "";
  if (isTrialPlan(plan)) return date ? t("studio.plan.capacityPausedTrial", { date }) : t("studio.plan.capacityPausedTrialNoDate");
  return date ? t("studio.plan.capacityPaused", { date }) : t("studio.plan.capacityPausedNoDate");
}

/** Too few messages for this: "They reset on {date}", or on the trial "Your trial ends on {date}". */
export function notEnoughText(t, plan, resetsAt, locale) {
  const date = resetsAt ? formatDay(resetsAt, locale) : "";
  if (isTrialPlan(plan)) return date ? t("studio.chat.notEnoughTrial", { date }) : t("studio.chat.notEnoughTrialNoDate");
  return date ? t("studio.chat.notEnough", { date }) : t("studio.chat.notEnoughNoDate");
}

/**
 * What the chat shows as the client's message. For Continue and Fix what
 * broke the Studio stores a fixed English label, so the page words them in
 * its own language from `kind`.
 */
export const userMessageText = (t, message) => {
  if (message?.kind === "continue") return t("studio.chat.continueLabel");
  if (message?.kind === "fix") return t("studio.chat.fixLabel");
  return message?.text ?? "";
};

/**
 * How a finished turn ended, in the page's language, or null when it simply
 * finished. `turn` is the stored message or the "done" event; on a plan turn
 * it carries notCountedReason / counted (and endReason on the "done" event;
 * GET .../messages has no endReason, but its errorCode is the end reason
 * after a gateway refusal: "work_limit" or "capacity"). `trial` picks the
 * free trial's wording for the capacity pause.
 */
export function outcomeNote(t, outcome, errorCode, turn = null, { trial = false } = {}) {
  if (!outcome || outcome === "done") return null;
  const reason =
    turn?.endReason || (errorCode === "capacity" || errorCode === "work_limit" ? errorCode : null) || turn?.notCountedReason;
  // A work limit with no version saved: there is nothing to continue from.
  const nothingSaved =
    turn != null && (turn.notCountedReason === "nothing_saved" || ("version" in turn && turn.version == null));
  const limit = () => (nothingSaved ? t("studio.turn.limitNothingSaved") : t("studio.turn.limit"));
  if (reason === "capacity") return trial ? t("studio.turn.capacityTrial") : t("studio.turn.capacity");
  if (reason === "work_limit" || reason === "max_turns") return limit();
  // A credits turn (no `counted`) ran out of credits; on a plan turn the same
  // gateway refusal is the work limit.
  if (errorCode === "billing_error") {
    return typeof turn?.counted === "boolean" ? limit() : t("studio.turn.outOfCredits");
  }
  if (errorCode === "rate_limit" || errorCode === "overloaded") return t("studio.turn.aiBusy");
  if (outcome === "stopped") return t("studio.turn.stopped");
  if (outcome === "limit") return limit();
  return t("studio.turn.failed");
}

// Why a turn was not counted (notCountedReason), as studio.chat.counted.reasons.*.
export const NOT_COUNTED_REASONS = Object.freeze(["nothing_saved", "our_error", "deadline", "capacity"]);

/**
 * What a finished AI turn cost: "Used 1 message", "Used 2 messages
 * (Advanced)", "Free repair" or "Not counted: ...". null for a turn from
 * before plans (no `counted`), which used credits.
 */
export function countedNote(t, turn) {
  if (!turn || typeof turn.counted !== "boolean") return null;
  if (turn.counted) {
    if (turn.kind === "fix" || turn.fixUsed) return t("studio.chat.counted.fix");
    return Number(turn.messagesUsed) >= 2 ? t("studio.chat.counted.usedAdvanced") : t("studio.chat.counted.used");
  }
  const reason = NOT_COUNTED_REASONS.includes(turn.notCountedReason) ? turn.notCountedReason : "our_error";
  return t("studio.chat.counted.notCounted", { reason: t(`studio.chat.counted.reasons.${reason}`) });
}

/**
 * A plan price from the backend ({ amount in cents, currency }) with the
 * currency CODE spelled out ("USD 19.99" / "19,99 USD"): a bare "$" reads as
 * pesos to much of the Spanish-speaking audience.
 */
export const formatPlanPrice = (price, locale) => {
  if (!price || typeof price.amount !== "number" || !price.currency) return "";
  try {
    return new Intl.NumberFormat(locale || undefined, {
      style: "currency",
      currency: String(price.currency).toUpperCase(),
      currencyDisplay: "code",
    }).format(price.amount / 100);
  } catch {
    return `${(price.amount / 100).toFixed(2)} ${String(price.currency).toUpperCase()}`;
  }
};

/** A short day for meters: "Nov 28" / "28 nov". */
export const formatShortDay = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale || undefined, { month: "short", day: "numeric" }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/** A time of day, for "try again after 7:00 PM" (the trial's daily reset). */
export const formatTime = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale || undefined, { timeStyle: "short" }).format(new Date(iso));
  } catch {
    return iso;
  }
};

/** A calendar day only (renewal and cancellation dates). */
export const formatDay = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale || undefined, { dateStyle: "long" }).format(new Date(iso));
  } catch {
    return iso;
  }
};

export const formatDate = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Intl.DateTimeFormat(locale || undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso));
  } catch {
    return iso;
  }
};
